import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '../api';
import { paymentEndpoints } from '../api-payments';
import type { AdminTenderInput, AmountPolicy, OrderPaymentLine, OrderPaymentSummary, PastDuePolicy, PaymentRequestView } from '../api-payments';
import { usePermissions } from '../session';
import { loadStripe } from '../stripe';
import { Badge, Button, EmptyRow, ErrorNote, Field, Panel, Table, formatWhen } from '../ui';
import type { Tone } from '../ui';
import { INPUT, dollars, humanize } from './event/shared';

/**
 * The Payment panel on an order (D-71, docs/payment-requests.md §7): what the
 * order is worth, what was paid and what is still owed; every payment with
 * its method, Stripe fee and payout; the payment links with their trail; and
 * the actions: collect a payment, send / resend / copy / revoke a link, change
 * the due date, cancel the unpaid order.
 */

export const paymentStatusTone = (status: string): Tone => {
  switch (status) {
    case 'PAID':
      return 'ok';
    case 'PARTIALLY_PAID':
      return 'accent';
    case 'UNPAID':
    case 'PAYMENT_FAILED':
      return 'warn';
    case 'OVERPAID':
    case 'REFUND_PENDING':
      return 'bad';
    default:
      return 'neutral';
  }
};

const cents = (text: string): number | null => {
  const value = Number(text.replace(/[$,\s]/g, ''));
  return Number.isFinite(value) && value > 0 ? Math.round(value * 100) : null;
};

const toIso = (date: string): string | null => (date ? new Date(`${date}T23:59:59`).toISOString() : null);

export function methodLabel(p: Pick<OrderPaymentLine, 'tender_type' | 'payment_method' | 'underlying_card_brand' | 'card_brand' | 'card_last4' | 'check_number'>): string {
  if (p.tender_type === 'CHECK') return `Check${p.check_number ? ` #${p.check_number}` : ''}`;
  if (p.tender_type === 'CASH') return 'Cash';
  if (p.tender_type === 'HOUSE_ACCOUNT') return 'House account';
  if (p.tender_type === 'GIFT_CARD') return 'Gift card';
  if (p.tender_type === 'LOYALTY_REWARD') return 'Loyalty reward';
  const method = p.payment_method ? humanize(p.payment_method) : humanize(p.tender_type);
  const brand = p.underlying_card_brand ?? p.card_brand;
  return `${method}${brand ? ` · ${brand.toUpperCase()}` : ''}${p.card_last4 ? ` ····${p.card_last4}` : ''}`;
}

export function OrderPaymentPanel({ orderId, orderEmail, orderName, onNotice }: { orderId: string; orderEmail: string | null; orderName: string | null; onNotice: (message: string) => void }) {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const summary = useQuery<OrderPaymentSummary, ApiError>({ queryKey: ['orders', 'payment', orderId], queryFn: () => paymentEndpoints.summary(orderId) });
  const [mode, setMode] = useState<'none' | 'collect' | 'link' | 'terms' | 'cancel'>('none');
  const [error, setError] = useState<string | null>(null);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['orders'] });
  const fail = (cause: ApiError | Error) => setError(cause.message);

  const resend = useMutation({ mutationFn: ({ id, rotate }: { id: string; rotate: boolean }) => paymentEndpoints.resendRequest(id, rotate), onSuccess: (r) => { setError(null); onNotice(r.url ? 'A new link was emailed; the old link no longer works.' : 'Payment link emailed again.'); refresh(); }, onError: fail });
  const copy = useMutation({
    mutationFn: (id: string) => paymentEndpoints.copyRequest(id),
    onSuccess: async (r) => {
      setError(null);
      try {
        await navigator.clipboard.writeText(r.url);
        onNotice('Payment link copied to the clipboard.');
      } catch {
        window.prompt('Copy the payment link', r.url);
      }
      refresh();
    },
    onError: fail,
  });
  const revoke = useMutation({ mutationFn: ({ id, reason }: { id: string; reason: string | null }) => paymentEndpoints.revokeRequest(id, reason), onSuccess: () => { setError(null); onNotice('Payment link revoked; it no longer works.'); refresh(); }, onError: fail });

  if (summary.error) return <ErrorNote>{summary.error.message}</ErrorNote>;
  const s = summary.data;
  if (!s) return null;
  const owing = s.balance.balance_due_cents > 0;
  const live = s.requests.find((r) => r.status === 'ACTIVE' || r.status === 'PARTIALLY_PAID') ?? null;
  const committed = s.payment_status !== 'VOID' && s.balance.amount_due_cents > 0;

  return (
    <Panel
      title="Payment"
      description={s.paid_in_full_at ? `Paid in full ${formatWhen(s.paid_in_full_at)}` : owing ? `Issuance: ${humanize(s.ticket_issuance_policy)} · past due: ${humanize(s.past_due_policy)}` : ''}
      actions={
        <div className="flex flex-wrap gap-2">
          {owing && can('orders.payments.collect') ? <Button variant="primary" onClick={() => setMode(mode === 'collect' ? 'none' : 'collect')}>Collect payment…</Button> : null}
          {owing && can('orders.payment_requests.manage') && !live ? <Button onClick={() => setMode(mode === 'link' ? 'none' : 'link')}>Send payment link…</Button> : null}
          {owing && can('orders.balance_due') ? <Button onClick={() => setMode(mode === 'terms' ? 'none' : 'terms')}>Edit terms…</Button> : null}
          {owing && committed && can('orders.cancel_unpaid') ? <Button variant="danger" onClick={() => setMode(mode === 'cancel' ? 'none' : 'cancel')}>Cancel unpaid order…</Button> : null}
        </div>
      }
    >
      <div className="space-y-4 px-5 py-4">
        {error ? <ErrorNote>{error}</ErrorNote> : null}
        <div className="flex flex-wrap items-end gap-6">
          <Stat label="Order total" value={dollars(s.balance.amount_due_cents)} />
          <Stat label="Paid" value={dollars(s.balance.paid_cents)} detail={s.balance.refunds_cents ? `${dollars(s.balance.refunds_cents)} refunded` : undefined} />
          <Stat label="Balance due" value={dollars(Math.max(0, s.balance.balance_due_cents))} tone={owing ? (s.past_due ? 'bad' : 'warn') : 'ok'} detail={s.balance.in_flight_cents ? `${dollars(s.balance.in_flight_cents)} in progress` : undefined} />
          <Stat label="Due" value={s.payment_due_at ? s.payment_due_at.slice(0, 10) : '—'} tone={s.past_due ? 'bad' : undefined} detail={s.past_due ? `${s.days_past_due} day${s.days_past_due === 1 ? '' : 's'} past due` : undefined} />
          <div>
            <Badge tone={paymentStatusTone(s.payment_status)}>{humanize(s.payment_status)}</Badge>
            {s.past_due ? <span className="ml-1"><Badge tone="bad">past due</Badge></span> : null}
          </div>
        </div>

        {mode === 'collect' ? <CollectForm orderId={orderId} balance={s.balance.balance_due_cents - s.balance.in_flight_cents} canHouse={can('box_office.house_account')} onDone={(m) => { setMode('none'); onNotice(m); refresh(); }} onCancel={() => setMode('none')} /> : null}
        {mode === 'link' ? <LinkForm orderId={orderId} balance={s.balance.balance_due_cents} email={orderEmail} name={orderName} dueAt={s.payment_due_at} onDone={(m) => { setMode('none'); onNotice(m); refresh(); }} onCancel={() => setMode('none')} /> : null}
        {mode === 'terms' ? <TermsForm orderId={orderId} summary={s} onDone={(m) => { setMode('none'); onNotice(m); refresh(); }} onCancel={() => setMode('none')} /> : null}
        {mode === 'cancel' ? <CancelForm orderId={orderId} paid={s.balance.paid_cents} onDone={(m) => { setMode('none'); onNotice(m); refresh(); }} onCancel={() => setMode('none')} /> : null}

        <Table headers={['When', 'Method', 'Amount', 'Refunded', 'Status', 'Stripe fee', 'Payout', 'By']}>
          {s.payments.length === 0 ? (
            <EmptyRow columns={8}>No payments yet.</EmptyRow>
          ) : (
            s.payments.map((p) => (
              <tr key={p.id} className="border-b border-line last:border-0">
                <td className="px-5 py-2 text-sm whitespace-nowrap">{formatWhen(p.captured_at ?? p.created_at)}</td>
                <td className="px-5 py-2 text-sm">
                  {methodLabel(p)}
                  <div className="text-xs text-ink-400">
                    {p.payment_method_family ? humanize(p.payment_method_family) : ''}
                    {p.plan_purpose === 'BALANCE_PAYMENT' ? ' · balance payment' : ''}
                    {p.payment_request_id ? ' · via link' : ''}
                  </div>
                </td>
                <td className="tabular px-5 py-2 text-sm">{dollars(p.captured_amount_cents || p.amount_cents)}</td>
                <td className="tabular px-5 py-2 text-sm">{p.refunded_amount_cents ? dollars(p.refunded_amount_cents) : ''}</td>
                <td className="px-5 py-2 text-sm">{humanize(p.status)}</td>
                <td className="tabular px-5 py-2 text-sm">{p.stripe_fee_cents === null ? (p.processor === 'STRIPE' ? 'pending' : '') : dollars(p.stripe_fee_cents)}</td>
                <td className="px-5 py-2 text-xs">{p.stripe_payout_id ? `${p.stripe_payout_id} · ${p.payout_arrival_date ?? ''} · ${humanize(p.payout_reconciliation_status ?? '')}` : ''}</td>
                <td className="px-5 py-2 text-sm">{p.staff_name ?? (p.payment_request_id ? 'Customer' : '')}</td>
              </tr>
            ))
          )}
        </Table>

        {s.requests.length > 0 ? (
          <div>
            <h3 className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Payment links</h3>
            <ul className="mt-2 divide-y divide-line rounded border border-line text-sm">
              {s.requests.map((r) => (
                <RequestRow
                  key={r.id}
                  request={r}
                  manage={can('orders.payment_requests.manage')}
                  busy={resend.isPending || copy.isPending || revoke.isPending}
                  onResend={(rotate) => resend.mutate({ id: r.id, rotate })}
                  onCopy={() => copy.mutate(r.id)}
                  onRevoke={() => {
                    const reason = window.prompt('Why is this link being revoked? (optional)');
                    if (reason !== null) revoke.mutate({ id: r.id, reason: reason.trim() || null });
                  }}
                />
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </Panel>
  );
}

function Stat({ label, value, detail, tone }: { label: string; value: string; detail?: string | undefined; tone?: Tone | undefined }) {
  const color = tone === 'bad' ? 'text-bad' : tone === 'warn' ? 'text-warn' : tone === 'ok' ? 'text-ok' : 'text-ink-900';
  return (
    <div>
      <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">{label}</p>
      <p className={`tabular text-lg font-semibold ${color}`}>{value}</p>
      {detail ? <p className="text-xs text-ink-400">{detail}</p> : null}
    </div>
  );
}

function RequestRow({ request: r, manage, busy, onResend, onCopy, onRevoke }: { request: PaymentRequestView; manage: boolean; busy: boolean; onResend: (rotate: boolean) => void; onCopy: () => void; onRevoke: () => void }) {
  const [open, setOpen] = useState(false);
  const live = r.status === 'ACTIVE' || r.status === 'PARTIALLY_PAID';
  return (
    <li className="px-3 py-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <Badge tone={live ? 'accent' : r.status === 'PAID' ? 'ok' : 'neutral'}>{humanize(r.status)}</Badge>{' '}
          <span className="text-ink-900">{r.recipient_email_snapshot}</span>
          <span className="text-ink-400">
            {' '}· {humanize(r.amount_policy)} · {dollars(r.requested_amount_cents)}
            {r.sent_at ? ` · sent ${formatWhen(r.sent_at)}${r.send_count > 1 ? ` (${r.send_count}×)` : ''}` : ' · not emailed'}
            {r.last_viewed_at ? ` · viewed ${formatWhen(r.last_viewed_at)}` : ' · not opened'}
            {r.expires_at ? ` · expires ${formatWhen(r.expires_at)}` : ''}
          </span>
        </div>
        <div className="flex flex-wrap gap-1">
          <Button onClick={() => setOpen(!open)}>{open ? 'Hide trail' : 'Trail'}</Button>
          {manage && live ? (
            <>
              <Button disabled={busy} onClick={() => onResend(false)}>Resend</Button>
              <Button disabled={busy} onClick={() => { if (window.confirm('Send a brand-new link? The old link stops working.')) onResend(true); }}>New link</Button>
              <Button disabled={busy} onClick={onCopy}>Copy link</Button>
              <Button variant="danger" disabled={busy} onClick={onRevoke}>Revoke</Button>
            </>
          ) : null}
        </div>
      </div>
      {open ? (
        <ul className="mt-2 space-y-0.5 text-xs text-ink-600">
          {r.events.map((e, i) => (
            <li key={i}>
              {formatWhen(e.occurred_at)} · {humanize(e.event_type)}
              {e.amount_cents ? ` · ${dollars(e.amount_cents)}` : ''} · {e.staff_name ?? humanize(e.actor_type)}
              {e.source ? ` (${e.source})` : ''}
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

type Kind = 'CHECK' | 'HOUSE_ACCOUNT' | 'CARD_KEYED';

/** Collect toward the balance: a check, a house account, or a keyed card authorized here first. */
function CollectForm({ orderId, balance, canHouse, onDone, onCancel }: { orderId: string; balance: number; canHouse: boolean; onDone: (message: string) => void; onCancel: () => void }) {
  const [kind, setKind] = useState<Kind>('CHECK');
  const [amount, setAmount] = useState((Math.max(0, balance) / 100).toFixed(2));
  const [checkNumber, setCheckNumber] = useState('');
  const [orgId, setOrgId] = useState('');
  const [note, setNote] = useState('');
  const [receipt, setReceipt] = useState(true);
  const [card, setCard] = useState<{ intent_id: string; amount_cents: number; ready: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const elementsRef = useRef<{ stripe: Awaited<ReturnType<typeof loadStripe>>; elements: ReturnType<Awaited<ReturnType<typeof loadStripe>>['elements']> } | null>(null);

  const startCard = async () => {
    setError(null);
    const value = cents(amount);
    if (!value) return setError('Enter the amount');
    try {
      const started = await paymentEndpoints.startCard(orderId, value);
      if (!started.client_secret || !started.publishable_key) throw new Error('Stripe did not return a card form');
      const stripe = await loadStripe(started.publishable_key);
      const elements = stripe.elements({ clientSecret: started.client_secret });
      elementsRef.current = { stripe, elements };
      setCard({ intent_id: started.payment_intent_id, amount_cents: value, ready: false });
      window.setTimeout(() => elements.create('payment', { layout: 'tabs' }).mount(`#collect-card-${orderId}`), 0);
    } catch (cause) {
      setError((cause as Error).message);
    }
  };
  const authorize = async () => {
    if (!elementsRef.current || !card) return;
    const { stripe, elements } = elementsRef.current;
    const result = await stripe.confirmPayment({ elements, confirmParams: { return_url: window.location.href }, redirect: 'if_required' });
    if (result.error) return setError(result.error.message ?? 'The card was declined');
    setCard({ ...card, ready: true });
  };

  const collect = useMutation({
    mutationFn: () => {
      const value = cents(amount);
      if (!value) throw new Error('Enter the amount');
      let tender: AdminTenderInput;
      if (kind === 'CARD_KEYED') {
        if (!card?.ready) throw new Error('Authorize the card first');
        tender = { tender_type: 'CARD_KEYED', amount_cents: card.amount_cents, payment_intent_id: card.intent_id };
      } else if (kind === 'CHECK') {
        tender = { tender_type: 'CHECK', amount_cents: value, check_number: checkNumber.trim() || null };
      } else {
        if (!orgId.trim()) throw new Error('Enter the organization id for the house account');
        tender = { tender_type: 'HOUSE_ACCOUNT', amount_cents: value, organization_id: orgId.trim() };
      }
      return paymentEndpoints.collect(orderId, { tenders: [tender], receipt_email: receipt, note: note.trim() || null });
    },
    onSuccess: (r) => onDone(`Collected ${dollars(r.payment.amount_cents)}. ${r.payment.paid_in_full ? 'The order is paid in full.' : `${dollars(r.payment.balance_after_cents)} still due.`}`),
    onError: (cause: Error) => setError(cause.message),
  });

  return (
    <div className="space-y-3 rounded border border-line bg-page p-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Method" htmlFor="collect-kind">
          <select id="collect-kind" className={INPUT} value={kind} onChange={(e) => { setKind(e.target.value as Kind); setCard(null); }}>
            <option value="CHECK">Check</option>
            <option value="CARD_KEYED">Card (keyed)</option>
            {canHouse ? <option value="HOUSE_ACCOUNT">House account</option> : null}
          </select>
        </Field>
        <Field label="Amount" htmlFor="collect-amount" hint={`Balance ${dollars(balance)}`}>
          <input id="collect-amount" className={INPUT} value={amount} disabled={Boolean(card)} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
        </Field>
        {kind === 'CHECK' ? (
          <Field label="Check number" htmlFor="collect-check">
            <input id="collect-check" className={INPUT} value={checkNumber} onChange={(e) => setCheckNumber(e.target.value)} />
          </Field>
        ) : null}
        {kind === 'HOUSE_ACCOUNT' ? (
          <Field label="Organization id" htmlFor="collect-org">
            <input id="collect-org" className={INPUT} value={orgId} onChange={(e) => setOrgId(e.target.value)} />
          </Field>
        ) : null}
        <Field label="Note" htmlFor="collect-note">
          <input id="collect-note" className={INPUT} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <label className="flex items-center gap-1 text-sm text-ink-600">
          <input type="checkbox" checked={receipt} onChange={(e) => setReceipt(e.target.checked)} /> Email receipt
        </label>
      </div>
      {kind === 'CARD_KEYED' ? (
        card ? (
          <div className="space-y-2">
            <div id={`collect-card-${orderId}`} className="max-w-md" />
            {!card.ready ? <Button onClick={() => void authorize()}>Authorize {dollars(card.amount_cents)}</Button> : <Badge tone="ok">card authorized</Badge>}
          </div>
        ) : (
          <Button onClick={() => void startCard()}>Enter card…</Button>
        )
      ) : null}
      <div className="flex gap-2">
        <Button variant="primary" disabled={collect.isPending || (kind === 'CARD_KEYED' && !card?.ready)} onClick={() => collect.mutate()}>
          {collect.isPending ? 'Recording…' : 'Record payment'}
        </Button>
        <Button onClick={onCancel}>Close</Button>
      </div>
    </div>
  );
}

export function LinkFields({ value, onChange, balance }: { value: { policy: AmountPolicy; amount: string; minimum: string; expires: string }; onChange: (next: { policy: AmountPolicy; amount: string; minimum: string; expires: string }) => void; balance: number }) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <Field label="Customer may pay" htmlFor="link-policy">
        <select id="link-policy" className={INPUT} value={value.policy} onChange={(e) => onChange({ ...value, policy: e.target.value as AmountPolicy })}>
          <option value="FULL_REMAINING_BALANCE_ONLY">The full balance</option>
          <option value="CUSTOM_AMOUNT_ALLOWED">Any amount up to the balance</option>
          <option value="AT_LEAST_MINIMUM">At least a minimum</option>
          <option value="FIXED_REQUEST_AMOUNT">A fixed amount</option>
        </select>
      </Field>
      {value.policy === 'FIXED_REQUEST_AMOUNT' ? (
        <Field label="Amount" htmlFor="link-amount" hint={balance ? `Balance ${dollars(balance)}` : undefined}>
          <input id="link-amount" className={INPUT} value={value.amount} onChange={(e) => onChange({ ...value, amount: e.target.value })} inputMode="decimal" />
        </Field>
      ) : null}
      {value.policy === 'AT_LEAST_MINIMUM' ? (
        <Field label="Minimum" htmlFor="link-minimum">
          <input id="link-minimum" className={INPUT} value={value.minimum} onChange={(e) => onChange({ ...value, minimum: e.target.value })} inputMode="decimal" />
        </Field>
      ) : null}
      <Field label="Link expires" htmlFor="link-expires" hint="Optional">
        <input id="link-expires" type="date" className={INPUT} value={value.expires} onChange={(e) => onChange({ ...value, expires: e.target.value })} />
      </Field>
    </div>
  );
}

export const linkInput = (value: { policy: AmountPolicy; amount: string; minimum: string; expires: string }) => ({
  amount_policy: value.policy,
  requested_amount_cents: value.policy === 'FIXED_REQUEST_AMOUNT' ? cents(value.amount) : null,
  minimum_amount_cents: value.policy === 'AT_LEAST_MINIMUM' ? cents(value.minimum) : null,
  expires_at: toIso(value.expires),
});

function LinkForm({ orderId, balance, email, name, dueAt, onDone, onCancel }: { orderId: string; balance: number; email: string | null; name: string | null; dueAt: string | null; onDone: (message: string) => void; onCancel: () => void }) {
  const [to, setTo] = useState(email ?? '');
  const [link, setLink] = useState({ policy: 'FULL_REMAINING_BALANCE_ONLY' as AmountPolicy, amount: '', minimum: '', expires: '' });
  const [send, setSend] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () => paymentEndpoints.createRequest(orderId, { ...linkInput(link), recipient_email: to.trim() || null, recipient_name: name, due_at: dueAt, send }),
    onSuccess: async (r) => {
      if (!send) {
        try {
          await navigator.clipboard.writeText(r.url);
        } catch {
          window.prompt('Copy the payment link', r.url);
        }
      }
      onDone(send ? `Payment link emailed to ${r.payment_request.recipient_email_snapshot}.` : 'Payment link created and copied to the clipboard.');
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  return (
    <div className="space-y-3 rounded border border-line bg-page p-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Email to" htmlFor="link-to">
          <input id="link-to" type="email" className={INPUT} value={to} onChange={(e) => setTo(e.target.value)} />
        </Field>
        <LinkFields value={link} onChange={setLink} balance={balance} />
        <label className="flex items-center gap-1 text-sm text-ink-600">
          <input type="checkbox" checked={send} onChange={(e) => setSend(e.target.checked)} /> Email it now
        </label>
      </div>
      <div className="flex gap-2">
        <Button variant="primary" disabled={create.isPending} onClick={() => create.mutate()}>
          {send ? 'Send link' : 'Create and copy link'}
        </Button>
        <Button onClick={onCancel}>Close</Button>
      </div>
    </div>
  );
}

function TermsForm({ orderId, summary, onDone, onCancel }: { orderId: string; summary: OrderPaymentSummary; onDone: (message: string) => void; onCancel: () => void }) {
  const [due, setDue] = useState(summary.payment_due_at ? summary.payment_due_at.slice(0, 10) : '');
  const [policy, setPolicy] = useState<PastDuePolicy>(summary.past_due_policy);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({ mutationFn: () => paymentEndpoints.terms(orderId, { payment_due_at: toIso(due), past_due_policy: policy }), onSuccess: () => onDone('Payment terms updated.'), onError: (cause: ApiError) => setError(cause.message) });
  return (
    <div className="space-y-3 rounded border border-line bg-page p-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Due date" htmlFor="terms-due">
          <input id="terms-due" type="date" className={INPUT} value={due} onChange={(e) => setDue(e.target.value)} />
        </Field>
        <Field label="When past due" htmlFor="terms-policy">
          <select id="terms-policy" className={INPUT} value={policy} onChange={(e) => setPolicy(e.target.value as PastDuePolicy)}>
            <option value="NO_AUTOMATIC_ACTION">No automatic action</option>
            <option value="NOTIFY_STAFF">Notify staff</option>
            <option value="AUTO_CANCEL_AFTER_GRACE_PERIOD">Auto-cancel after grace period</option>
          </select>
        </Field>
      </div>
      <div className="flex gap-2">
        <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate()}>Save terms</Button>
        <Button onClick={onCancel}>Close</Button>
      </div>
    </div>
  );
}

function CancelForm({ orderId, paid, onDone, onCancel }: { orderId: string; paid: number; onDone: (message: string) => void; onCancel: () => void }) {
  const [reason, setReason] = useState('CUSTOMER_REQUEST');
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const cancel = useMutation({ mutationFn: () => paymentEndpoints.cancelBalance(orderId, { reason_code: reason, reason_text: text.trim() || null }), onSuccess: () => onDone(paid > 0 ? `Order canceled; ${dollars(paid)} refunded to the tenders it was paid with.` : 'Order canceled; the seats are back on sale.'), onError: (cause: ApiError) => setError(cause.message) });
  return (
    <div className="space-y-3 rounded border border-bad/30 bg-bad-soft/40 p-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      <p className="text-sm text-ink-900">Cancels the order, voids its tickets, releases the seats and revokes any payment link.{paid > 0 ? ` The ${dollars(paid)} already paid is refunded (Stripe newest first, then cash, check, house account, gift card, reward).` : ''}</p>
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Reason" htmlFor="cancel-reason">
          <select id="cancel-reason" className={INPUT} value={reason} onChange={(e) => setReason(e.target.value)}>
            {['CUSTOMER_REQUEST', 'EVENT_CANCELLED', 'EVENT_POSTPONED', 'DUPLICATE_PURCHASE', 'SERVICE_RECOVERY', 'FRAUD', 'OTHER'].map((r) => (
              <option key={r} value={r}>{humanize(r)}</option>
            ))}
          </select>
        </Field>
        <Field label="Note" htmlFor="cancel-note">
          <input id="cancel-note" className={INPUT} value={text} onChange={(e) => setText(e.target.value)} />
        </Field>
      </div>
      <div className="flex gap-2">
        <Button variant="danger" disabled={cancel.isPending} onClick={() => { if (window.confirm('Cancel this order?')) cancel.mutate(); }}>Cancel order</Button>
        <Button onClick={onCancel}>Keep order</Button>
      </div>
    </div>
  );
}
